import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import { toAssetUrl } from '../../utils/assetBase';
import PhoneInput from '../../components/PhoneInput';

// Public banket showcase — müştəri banketləri menyu formasında görür (25₼, 30₼...).
// Hər banketin içindəkilər kateqoriya ilə, SAYLAR göstərilmədən.

const BRAND = '#0090bb';
const money = (v) => `${Number(v || 0).toFixed(0)} ₼`;
const MIN_GUESTS = 25;   // banket minimum nəfər sayı
// Beh = cəm × faiz%, yuxarı 10-a yuvarlanır (serverlə eyni)
const depositOf = (total, percent) => Math.ceil((Number(total || 0) * (Number(percent || 0) / 100)) / 10) * 10;

export default function PublicBanquet() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('t') || '';

    const [data, setData] = useState([]);
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sel, setSel] = useState(null);   // seçilmiş banket id (null = kartlar siyahısı)

    // Banket sifarişi (beh ödənişi)
    const [orderOpen, setOrderOpen] = useState(false);
    const [order, setOrder] = useState({ guest_name: '', phone: '', guests: MIN_GUESTS });
    const [phoneValid, setPhoneValid] = useState(false);
    const [ordering, setOrdering] = useState(false);
    const [orderErr, setOrderErr] = useState('');

    const selected = data.find(b => b.id === sel) || null;
    const itemCount = (b) => (b.categories || []).reduce((s, c) => s + (c.items?.length || 0), 0);
    const openBanquet = (id) => { setSel(id); window.scrollTo({ top: 0 }); };

    const openOrder = () => {
        setOrder({ guest_name: '', phone: '', guests: MIN_GUESTS });
        setPhoneValid(false); setOrderErr(''); setOrderOpen(true);
    };
    const submitOrder = async () => {
        setOrderErr('');
        if (!order.guest_name.trim()) return setOrderErr('Adınızı yazın.');
        if (!phoneValid) return setOrderErr('Telefon nömrəsini tam və düzgün yazın.');
        const guests = Math.max(MIN_GUESTS, parseInt(order.guests, 10) || 0);
        if (guests < 1) return setOrderErr('Nəfər sayını yazın.');
        setOrdering(true);
        try {
            const res = await api.post('/public-booking/banquet-pay', {
                token, set_id: selected.id, guests,
                guest_name: order.guest_name, phone: order.phone,
            });
            if (res.data?.payment_url) { window.location.href = res.data.payment_url; return; }
            setOrderErr('Ödəniş başladıla bilmədi. Yenidən cəhd edin.');
        } catch (err) {
            setOrderErr(err.response?.data?.msg || 'Göndərilmədi. Yenidən cəhd edin.');
        } finally {
            setOrdering(false);
        }
    };

    const load = useCallback(async () => {
        if (!token) { setError('Link yanlışdır.'); setLoading(false); return; }
        setLoading(true); setError('');
        try {
            const res = await api.get('/banquet/public', { params: { token } });
            setData(res.data.data || []);
            setTenant(res.data.tenant || null);
        } catch (err) {
            setError(err.response?.data?.msg || 'Yüklənmədi.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    // İki ayrı əlaqə: WhatsApp (yönləndirmə) və zəng (tel:)
    const waLink = tenant?.whatsapp
        ? `https://wa.me/${String(tenant.whatsapp).replace(/\D/g, '').replace(/^0/, '994')}`
        : '';
    const callLink = tenant?.phone ? `tel:${String(tenant.phone).replace(/\s/g, '')}` : '';

    // Zəng et + WhatsApp düymələri (grid və detalda təkrar istifadə olunur)
    const contactButtons = (
        (callLink || waLink) && (
            <div className="flex gap-2">
                {callLink && (
                    <a href={callLink}
                        className="flex flex-1 items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-black uppercase tracking-wider text-white"
                        style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                        📞 Zəng et
                    </a>
                )}
                {waLink && (
                    <a href={waLink} target="_blank" rel="noopener noreferrer"
                        className="flex flex-1 items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-black uppercase tracking-wider text-white"
                        style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                        💬 WhatsApp
                    </a>
                )}
            </div>
        )
    );

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur"
                 style={{ paddingTop: 'env(safe-area-inset-top)' }}>
                <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
                    {tenant?.logo_url && (
                        <img src={toAssetUrl(tenant.logo_url)} alt="" className="h-9 w-9 rounded-lg object-cover" />
                    )}
                    <div className="min-w-0">
                        <h1 className="truncate text-base font-black text-gray-900">{tenant?.name || 'Banket menyu'}</h1>
                        <p className="text-[11px] text-gray-400">Banket menyularımız</p>
                    </div>
                    {token && (
                        <a href={`/qrmenu?t=${encodeURIComponent(token)}`}
                            className="ml-auto shrink-0 whitespace-nowrap rounded-xl border px-3 py-2 text-xs font-bold transition-all active:scale-95"
                            style={{ borderColor: BRAND, color: BRAND }}>
                            ← Menyu
                        </a>
                    )}
                </div>
            </div>

            <div className="mx-auto max-w-3xl px-4 py-4">
                {!loading && !error && data.length > 0 && (
                    <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-center text-[12px] font-semibold text-amber-800">
                        ℹ️ Bu qiymətlər <b>minimum {MIN_GUESTS} nəfər</b> üçün nəzərdə tutulub.
                    </div>
                )}
                {loading ? (
                    <div className="py-20 text-center text-sm text-gray-400">Yüklənir...</div>
                ) : error ? (
                    <div className="rounded-2xl border border-red-100 bg-red-50 py-12 text-center text-sm font-semibold text-red-600">{error}</div>
                ) : data.length === 0 ? (
                    <div className="py-20 text-center text-sm text-gray-400">Banket menyu yoxdur.</div>
                ) : selected ? (
                    /* ── DETAL: seçilmiş banketin içi ── */
                    <div className="space-y-4">
                        <button type="button" onClick={() => setSel(null)}
                            className="flex items-center gap-1.5 text-sm font-bold" style={{ color: BRAND }}>
                            ← Banketlər
                        </button>

                        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                            {selected.image_url && (
                                <img src={toAssetUrl(selected.image_url)} alt={selected.name} className="h-44 w-full object-cover" />
                            )}
                            <div className="p-4">
                                <div className="mb-3 flex items-center justify-between gap-2">
                                    <h2 className="text-lg font-black text-gray-900">{selected.name}</h2>
                                    <span className="whitespace-nowrap rounded-full px-3 py-1 text-sm font-black text-white"
                                        style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                                        {money(selected.price_per_person)}<span className="text-[10px] font-normal opacity-80">/nəfər</span>
                                    </span>
                                </div>

                                {selected.categories.length === 0 ? (
                                    <p className="text-sm text-gray-400">Məhsullar tezliklə əlavə olunacaq.</p>
                                ) : (
                                    <div className="space-y-5">
                                        {selected.categories.map((cat, i) => (
                                            <div key={i}>
                                                <div className="mb-2.5 flex items-center gap-2 border-b-2 pb-1.5" style={{ borderColor: `${BRAND}33` }}>
                                                    <span className="inline-block h-4 w-1.5 rounded-full" style={{ background: BRAND }} />
                                                    <span className="text-sm font-black uppercase tracking-wide" style={{ color: BRAND }}>{cat.name}</span>
                                                    <span className="ml-auto text-[10px] font-semibold text-gray-300">{cat.items.length}</span>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                                    {cat.items.map((it, j) => (
                                                        <div key={j} className="text-center">
                                                            {it.image_url ? (
                                                                <img src={toAssetUrl(it.image_url)} alt={it.name}
                                                                    className="aspect-square w-full rounded-xl border border-gray-100 object-cover" />
                                                            ) : (
                                                                <div className="flex aspect-square w-full items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 text-2xl text-gray-300">🍽️</div>
                                                            )}
                                                            <div className="mt-1 text-[11px] font-medium leading-tight text-gray-700">{it.name}</div>
                                                            {it.note && <div className="text-[9px] leading-tight text-gray-400">{it.note}</div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {tenant?.payriff_enabled && (
                            <button type="button" onClick={openOrder}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-black uppercase tracking-wider text-white"
                                style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                                💳 Sifariş ver (beh ödə)
                            </button>
                        )}
                        {contactButtons}
                        <p className="pb-4 text-center text-[11px] text-gray-400">Qiymət 1 nəfər üçündür. Rezervasiya üçün bizimlə əlaqə saxlayın.</p>
                    </div>
                ) : (
                    /* ── SİYAHI: banket kartları (kvadrat seçimlər) ── */
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            {data.map((b) => (
                                <button key={b.id} type="button" onClick={() => openBanquet(b.id)}
                                    className="overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-sm transition-all active:scale-[0.98]">
                                    <div className="relative aspect-[4/3] w-full bg-gray-100">
                                        {b.image_url ? (
                                            <img src={toAssetUrl(b.image_url)} alt={b.name} className="h-full w-full object-cover" />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center text-4xl"
                                                style={{ background: `linear-gradient(135deg, ${BRAND}22, #006f9122)` }}>🍽️</div>
                                        )}
                                        <span className="absolute right-2 top-2 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-black text-white shadow"
                                            style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                                            {money(b.price_per_person)}<span className="text-[9px] font-normal opacity-80">/nəfər</span>
                                        </span>
                                    </div>
                                    <div className="p-3">
                                        <div className="text-sm font-black leading-tight text-gray-900">{b.name}</div>
                                        <div className="mt-1 flex items-center justify-between">
                                            <span className="text-[11px] text-gray-400">{itemCount(b)} məhsul</span>
                                            <span className="text-[11px] font-bold" style={{ color: BRAND }}>Bax →</span>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>

                        {contactButtons}
                        <p className="pb-4 text-center text-[11px] text-gray-400">Banketə toxunub içindəki yeməklərə baxın. Qiymət 1 nəfər üçündür.</p>
                    </div>
                )}
            </div>

            {/* ── SİFARİŞ PANELİ (beh ödənişi) ── */}
            {orderOpen && selected && (
                <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
                     onClick={(e) => e.target === e.currentTarget && setOrderOpen(false)}>
                    <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white sm:rounded-2xl">
                        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-5 pb-3"
                             style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
                            <div className="min-w-0">
                                <h3 className="truncate text-base font-black text-gray-900">🍽️ {selected.name}</h3>
                                <p className="text-[11px] text-gray-400">Banket sifarişi — {money(selected.price_per_person)}/nəfər</p>
                            </div>
                            <button type="button" onClick={() => setOrderOpen(false)} aria-label="Bağla"
                                className="-mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xl text-gray-400 active:bg-gray-100">✕</button>
                        </div>

                        <div className="space-y-3 p-5">
                            <input type="text" value={order.guest_name}
                                onChange={(e) => setOrder({ ...order, guest_name: e.target.value })}
                                placeholder="Ad, Soyad"
                                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none" />
                            <PhoneInput onChange={(full, valid) => { setOrder(o => ({ ...o, phone: full })); setPhoneValid(valid); }} />

                            {/* Nəfər sayı — +/− */}
                            {(() => {
                                const g = Math.max(MIN_GUESTS, parseInt(order.guests, 10) || MIN_GUESTS);
                                const setG = (v) => setOrder({ ...order, guests: Math.max(MIN_GUESTS, v) });
                                const total = Number(selected.price_per_person || 0) * g;
                                const dep = depositOf(total, tenant?.deposit_percent || 30);
                                return (
                                    <>
                                        <div className="rounded-xl border border-gray-200 p-3">
                                            <div className="flex items-center justify-between">
                                                <div className="text-sm font-bold text-gray-800">Neçə nəfər?</div>
                                                <div className="flex items-center gap-3">
                                                    <button type="button" onClick={() => setG(g - 1)} disabled={g <= MIN_GUESTS}
                                                        className="flex h-9 w-9 items-center justify-center rounded-full border-2 text-lg font-black transition-all disabled:opacity-30"
                                                        style={{ borderColor: BRAND, color: BRAND }}>−</button>
                                                    <input type="number" min="1" value={g}
                                                        onChange={(e) => setG(parseInt(e.target.value, 10) || 1)}
                                                        className="w-14 rounded-lg border border-gray-200 px-1 py-1 text-center text-lg font-black text-gray-900 focus:outline-none" />
                                                    <button type="button" onClick={() => setG(g + 1)}
                                                        className="flex h-9 w-9 items-center justify-center rounded-full border-2 text-lg font-black transition-all"
                                                        style={{ borderColor: BRAND, color: BRAND }}>+</button>
                                                </div>
                                            </div>
                                            <div className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-500">
                                                <div className="flex justify-between">
                                                    <span>{money(selected.price_per_person)} × {g} nəfər</span>
                                                    <span className="text-sm font-black" style={{ color: BRAND }}>{money(total)}</span>
                                                </div>
                                                <div className="mt-2 flex items-center justify-between rounded-lg bg-emerald-50 px-2.5 py-2 text-sm font-black text-emerald-700">
                                                    <span>💳 İndi ödəniləcək beh ({tenant?.deposit_percent || 30}%)</span>
                                                    <span>{money(dep)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {orderErr && <p className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{orderErr}</p>}

                                        <button type="button" onClick={submitOrder} disabled={ordering}
                                            className="w-full rounded-xl py-3 text-sm font-black text-white transition-all disabled:opacity-40"
                                            style={{ background: `linear-gradient(135deg, ${BRAND}, #006f91)` }}>
                                            {ordering ? 'Ödənişə yönləndirilir...' : `💳 ${money(dep)} beh ödə və sifariş ver`}
                                        </button>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
